import { AfterViewInit, Component, computed, DestroyRef, ElementRef, HostListener, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { isInIframe } from '@helpers/utils/is-in-iframe.util';
import { TopbarComponent } from '../../components/minisite/topbar/topbar.component';
import { NeighborsComponent } from '@components/minisite/topbar/neighbors/neighbors.component';
import { FooterComponent } from '../../components/minisite/footer/footer.component';
import { OverlayComponent } from '@components/minisite/overlay/overlay.component';
import { DvmService } from '@services/dvm.service';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { PortalService } from '@services/portal.service';
import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { MenuService } from '@services/components/menu.service';
import { BottomSheetComponent } from '@components/minisite/footer/bottom-sheet/bottom-sheet.component';
import { SearchService } from '@services/components/search.service';
import { BenefitsService } from '@services/benefits.service';
import { MenuContainerService, MenuFactoryService, MenuParamsService, MenuSelectionService } from '@services/menu';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-viewer-layout',
    imports: [
        TopbarComponent,
        NeighborsComponent,
        OverlayComponent,
        FooterComponent,
        BottomSheetComponent,
        CdkPortalOutlet
    ],
    viewProviders: [
        MenuService,
        MenuContainerService,
        MenuFactoryService,
        MenuParamsService,
        MenuSelectionService,
        SearchService,
        BenefitsService,
    ],
    templateUrl: './viewer-layout.component.html',
    styleUrl: './viewer-layout.component.css'
})
export class ViewerLayoutComponent implements AfterViewInit {

  // VIEW CHILDS
  @ViewChild('mainContainer', { static: true })
  private readonly mainContainer!: ElementRef<HTMLElement>;

  // HOST LISTENERS
  @HostListener('window:keydown', ['$event'])
  public handleFullScreen(event: KeyboardEvent): void {
    
    if (event.key === 'F11' && this.view() === AppViews.Pano) {
      event.preventDefault();
      this.dvmService.viewer3d.toggleFullscreen();
    }

  }

  // SERVICES
  private readonly dvmService:     DvmService              = inject(DvmService);
  private readonly portalService:  PortalService           = inject(PortalService);
  private readonly destroyRef:     DestroyRef              = inject(DestroyRef);

  // GLOBAL STATE
  private readonly view:           Signal<AppViews | null> = this.dvmService.view;

  // COMPONENT STATE
  public readonly  portalContent:  Signal<ComponentPortal<unknown>|null> = this.portalService.portalContent;
  public readonly  isPano:         Signal<boolean>          = computed((): boolean => this.view() === AppViews.Pano);
  public readonly  iframeMode:     boolean                  = isInIframe();
  // Scrims follow the same rule as the topbar text: top level (Topview/Main, or
  // view not yet determined) fades from white in light mode; Seatmap/Pano always
  // fade from black so the forced-white text has a backdrop (see template).
  protected readonly isTopLevelView: Signal<boolean>        = computed((): boolean => {
    const view: AppViews | null = this.view();
    return view !== AppViews.Seatmap && view !== AppViews.Pano;
  });
  protected readonly isFullscreen:  WritableSignal<boolean> = signal(false);

  // LIFECYCLE HOOKS
  ngAfterViewInit(): void {
    this.initComponent();
  }

  // METHODS
  /**
   * Inicializador del layout del viewer.
   */
  private initComponent(): void {

    this.overrideFullscreen();

    // init methods para el viewer layout...

  }

  /**
   * Sobreescribe el elemento del fullscreen del viewer y trackea su estado
   * para ocultar los scrims mientras el pano está en fullscreen.
   */
  private overrideFullscreen(): void {

    const viewer3d: Viewer3dService = this.dvmService.viewer3d;

    viewer3d.waitInitialize()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {

        viewer3d.interface.overrideFullscreenElement(this.mainContainer.nativeElement);

        viewer3d.getObservable('fullscreen_enabled').pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe((): void => this.isFullscreen.set(true));

        viewer3d.getObservable('fullscreen_disabled').pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe((): void => this.isFullscreen.set(false));

      });

  }

}
